/**
 * Firebase Web SDK initialisation for the customer-facing portal.
 *
 * Talks to the same Firebase project (`sitexpens`) as the mobile RN
 * client and the web-admin portal. Uses the standard Firebase Web
 * SDK (not the RN facade in `src/lib/firebase.ts`) because this app
 * runs in a browser.
 *
 * AUTH MODEL
 * The customer portal uses **MSG91 OTP** (NOT Firebase Phone Auth)
 * to mirror the mobile app's onboarding. A studio owner registered
 * with phone +91… on iOS signs into the web portal with the exact
 * same number and lands in the same studio.
 *
 * Flow:
 *   1. `openMsg91OtpModal({ widgetId, tokenAuth })` opens MSG91's
 *      hosted modal in the browser (MSG91 script preloaded in
 *      index.html).
 *   2. User enters phone + receives OTP + enters code, all inside
 *      MSG91's UI.
 *   3. On success MSG91 returns an `access-token` JWT.
 *   4. We call `mintCustomToken` callable (which already exists
 *      server-side in `functions/src/auth.ts`) with the access-token
 *      + phone. The function validates the token against MSG91's
 *      `verifyAccessToken` endpoint and returns a Firebase custom
 *      token.
 *   5. We sign into Firebase with `signInWithCustomToken`.
 *
 * Firebase Phone Auth + reCAPTCHA are NOT used here — removed
 * because (a) only Firebase-listed test phones could receive OTPs
 * without billing, (b) using MSG91 unifies the OTP UX with mobile,
 * (c) MSG91 has better Indian carrier deliverability than Firebase.
 */
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { openMsg91OtpModal } from './msg91';

// These keys are public by design — Firebase config is shipped to
// every web client. Security comes from rules + custom claims, not
// from hiding the project id. Same config as web-admin.
const firebaseConfig = {
  apiKey: 'AIzaSyCTXnw4GlafJIR1vZb9d02MLcF98nfPCyg',
  authDomain: 'sitexpens.firebaseapp.com',
  projectId: 'sitexpens',
  storageBucket: 'sitexpens.firebasestorage.app',
  messagingSenderId: '288864968221',
  appId: '1:288864968221:ios:d4d838777e1768526ff0f4',
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');

// MSG91 widget credentials — both PUBLIC, safe in browser. The real
// secret (used to verify the access-token MSG91 returns) lives on
// the server as `MSG91_AUTH_KEY` Firebase Secret.
//
// SOURCING: read from Vite env at build time. Falls back to empty
// string so the SignInPage shows a clear "not configured" error
// instead of crashing if the user hasn't set up the .env yet.
const MSG91_WEB_WIDGET_ID =
  (import.meta.env.VITE_MSG91_WEB_WIDGET_ID as string | undefined) ?? '';
const MSG91_WEB_TOKEN_AUTH =
  (import.meta.env.VITE_MSG91_WEB_TOKEN_AUTH as string | undefined) ?? '';

// ── Sign-in flow ────────────────────────────────────────────────
//
// Single entry point: `signInWithMsg91()`. Opens the MSG91 modal,
// waits for the user to complete OTP, exchanges the resulting
// access-token for a Firebase custom token, signs in. Resolves once
// the Firebase user is set; `AuthProvider`'s `onAuthStateChanged`
// listener picks up the new user and re-renders the app.

/** Returned by the `mintCustomToken` callable (defined in
 *  `functions/src/auth.ts`). */
type MintCustomTokenResult = { customToken: string };

const mintCustomToken = httpsCallable<
  { accessToken: string; phoneE164?: string; platform: 'web' },
  MintCustomTokenResult
>(functions, 'mintCustomToken');

export async function signInWithMsg91(): Promise<User> {
  if (!MSG91_WEB_WIDGET_ID || !MSG91_WEB_TOKEN_AUTH) {
    throw new Error(
      'Sign-in is not yet configured. Please contact support at hello@interioros.app.',
    );
  }
  // 1. Open MSG91's hosted modal — user enters phone, gets SMS,
  // enters OTP. Resolves with the access-token JWT. `phoneE164` is
  // typically empty on web (MSG91's web widget doesn't expose the
  // verified phone to the browser); that's OK — `mintCustomToken`
  // resolves it server-side via MSG91's `verifyAccessToken` call.
  const { accessToken, phoneE164 } = await openMsg91OtpModal({
    widgetId: MSG91_WEB_WIDGET_ID,
    tokenAuth: MSG91_WEB_TOKEN_AUTH,
  });

  // 2. Exchange access-token for Firebase custom token. We pass
  // `phoneE164` only when MSG91 gave us one (rare on web, always
  // on mobile) — server treats it as a hint and overrides with
  // MSG91's authoritative response when needed.
  const { data } = await mintCustomToken(
    phoneE164
      ? { accessToken, phoneE164, platform: 'web' }
      : { accessToken, platform: 'web' },
  );
  if (!data?.customToken) {
    throw new Error('Authentication failed. Please try again.');
  }

  // 3. Sign in with the Firebase custom token. `onAuthStateChanged`
  // fires the new user up to AuthProvider, which re-renders the app.
  const cred = await signInWithCustomToken(auth, data.customToken);
  return cred.user;
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

// Re-exports for consumers that prefer importing from one place.
export { onAuthStateChanged, httpsCallable };
export type { User };
